<script setup lang="ts">
import { ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import type { TableColumnsType } from 'ant-design-vue';
import type { CreditsLogItem } from '@shared/types';
import { getCreditsLogs } from '@/api/credits';

// ============================================
// Props / Emits
// ============================================

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ 'update:open': [value: boolean] }>();

// ============================================
// 状态
// ============================================

/** 当前激活的 Tab：consume=消费 income=收入 */
const activeTab = ref<'consume' | 'income'>('consume');

/** 消费 Tab 状态 */
const consumeState = ref({ list: [] as CreditsLogItem[], total: 0, page: 1, date: String(new Date().getFullYear()), loading: false });

/** 收入 Tab 状态 */
const incomeState = ref({ list: [] as CreditsLogItem[], total: 0, page: 1, date: String(new Date().getFullYear()), loading: false });

// 近两年年份选项
const yearOptions = (() => {
  const cur = new Date().getFullYear();
  return [cur, cur - 1].map(y => ({ label: String(y), value: String(y) }));
})();

// ============================================
// 表格列定义
// ============================================

const columns: TableColumnsType = [
  {
    title: '时间',
    dataIndex: 'created_at',
    width: 180,
    customRender: ({ text }: { text: string }) => text ? new Date(text).toLocaleString('zh-CN') : '-',
  },
  {
    title: '变动',
    dataIndex: 'number',
    width: 100,
    customRender: ({ text }: { text: number }) => text,
  },
  {
    title: '备注',
    dataIndex: 'info',
    ellipsis: true,
    customRender: ({ text }: { text?: string }) => text || '-',
  },
];

// ============================================
// 数据加载
// ============================================

/** 加载指定 Tab 的数据 */
async function loadData(tab: 'consume' | 'income') {
  const state = tab === 'consume' ? consumeState : incomeState;
  // 防重入
  if (state.value.loading) {
    return;
  }
  state.value.loading = true;
  try {
    const result = await getCreditsLogs({
      type: tab === 'consume' ? -1 : 1,
      page: state.value.page,
      perPage: 10,
      date: state.value.date,
    });
    state.value.list = result.data;
    state.value.total = result.total;
  } catch (err) {
    message.error('加载积分记录失败，请稍后重试');
  } finally {
    state.value.loading = false;
  }
}

/** Tab 切换 */
function handleTabChange(key: string) {
  activeTab.value = key as 'consume' | 'income';
  loadData(activeTab.value);
}

/** 年份切换 */
function handleYearChange(tab: 'consume' | 'income') {
  const state = tab === 'consume' ? consumeState : incomeState;
  state.value.page = 1;
  loadData(tab);
}

/** 分页切换 */
function handlePageChange(tab: 'consume' | 'income', page: number) {
  const state = tab === 'consume' ? consumeState : incomeState;
  state.value.page = page;
  loadData(tab);
}

// 弹窗打开时加载默认 Tab
watch(() => props.open, (val) => {
  if (val) {
    consumeState.value.page = 1;
    loadData('consume');
  }
});
</script>

<template>
  <a-modal
    :open="props.open"
    title="积分记录"
    :width="640"
    :footer="null"
    @update:open="emit('update:open', $event)"
  >
    <a-tabs :active-key="activeTab" @change="handleTabChange">
      <!-- 消费记录 Tab -->
      <a-tab-pane key="consume" tab="消费记录">
        <div class="tab-toolbar">
          <a-select
            v-model:value="consumeState.date"
            :options="yearOptions"
            style="width: 100px"
            @change="handleYearChange('consume')"
          />
        </div>
        <a-table
          :columns="columns"
          :data-source="consumeState.list"
          :loading="consumeState.loading"
          :pagination="false"
          row-key="_id"
          size="small"
        >
          <!-- 变动数量：红色负数（CSD 返回正数，消费 Tab 加负号） -->
          <template #bodyCell="{ column, text }">
            <template v-if="column.dataIndex === 'number'">
              <span style="color: #ff4d4f">-{{ text }}</span>
            </template>
          </template>
        </a-table>
        <div class="pagination-wrap">
          <a-pagination
            v-model:current="consumeState.page"
            :total="consumeState.total"
            :page-size="10"
            size="small"
            show-total
            @change="(page: number) => handlePageChange('consume', page)"
          />
        </div>
      </a-tab-pane>

      <!-- 充值/领取/退回 Tab -->
      <a-tab-pane key="income" tab="充值/领取/退回">
        <div class="tab-toolbar">
          <a-select
            v-model:value="incomeState.date"
            :options="yearOptions"
            style="width: 100px"
            @change="handleYearChange('income')"
          />
        </div>
        <a-table
          :columns="columns"
          :data-source="incomeState.list"
          :loading="incomeState.loading"
          :pagination="false"
          row-key="_id"
          size="small"
        >
          <!-- 变动数量：绿色正数 -->
          <template #bodyCell="{ column, text }">
            <template v-if="column.dataIndex === 'number'">
              <span style="color: #52c41a">+{{ text }}</span>
            </template>
          </template>
        </a-table>
        <div class="pagination-wrap">
          <a-pagination
            v-model:current="incomeState.page"
            :total="incomeState.total"
            :page-size="10"
            size="small"
            show-total
            @change="(page: number) => handlePageChange('income', page)"
          />
        </div>
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>

<style scoped>
.tab-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
